import {client} from '@/utils/gql'
import {
  Service_Request_Bool_Exp,
  Service_Request_Insert_Input,
  Service_Request_Order_By,
  Service_Request_Set_Input,
  ServiceRequestFragment,
  ServiceRequestWithTrademarksFragment,
} from '@/utils/graphqlSdk'
import {NotFoundError, ServerError} from '@/utils/server/errors'
import {WithRequiredProp} from '@/utils/types'

const getByIdOrNull = async (id: string): Promise<ServiceRequestFragment | null> =>
  (await client.serviceRequestGetById({id}))?.service_request_by_pk || null

const getById = async (id: string): Promise<ServiceRequestFragment> => {
  const request = await getByIdOrNull(id)

  if (!request) {
    throw new NotFoundError('Service request does not exist', {id})
  }

  return request
}

const getOneOrNull = async (
  filter: Service_Request_Bool_Exp,
): Promise<ServiceRequestFragment | null> => {
  const serviceRequest = (await client.serviceRequestGetMany({filter})).service_request

  if (serviceRequest.length > 1) {
    throw new ServerError('Get one returned multiple results', {filter})
  }

  return serviceRequest?.[0] ?? null
}

const getOne = async (filter: Service_Request_Bool_Exp): Promise<ServiceRequestFragment> => {
  const serviceRequest = await getOneOrNull(filter)

  if (!serviceRequest) {
    throw new NotFoundError('Get one returned null', {filter})
  }

  return serviceRequest
}

const getMany = async (
  filter: Service_Request_Bool_Exp,
  order_by?: Service_Request_Order_By,
  offset?: number,
  limit?: number,
): Promise<ServiceRequestFragment[]> => {
  return (
    await client.serviceRequestGetMany({
      filter,
      order_by,
      offset,
      limit,
    })
  ).service_request
}

const getManyWithTrademarks = async (
  filter: Service_Request_Bool_Exp,
  order_by?: Service_Request_Order_By,
  offset?: number,
  limit?: number,
): Promise<ServiceRequestWithTrademarksFragment[]> => {
  return (await client.serviceRequestGetManyWithTrademarks({filter, order_by, offset, limit}))
    .service_request
}

const updateOne = async (
  id: string,
  changes: WithRequiredProp<Service_Request_Set_Input, 'updated_by_user_id'>,
) => {
  const serviceRequest = (await client.serviceRequestUpdateOne({id, set: changes}))
    .update_service_request_by_pk

  if (!serviceRequest) {
    throw new ServerError('Service request update returned undefined', {id, changes})
  }

  return serviceRequest
}

const createOne = async (
  object: WithRequiredProp<Service_Request_Insert_Input, 'created_by_user_id'>,
): Promise<ServiceRequestFragment> => {
  const request = (await client.serviceRequestInsertOne({object})).insert_service_request_one

  if (!request) {
    throw new ServerError('Service request returned null after insert', {object})
  }

  return request
}

const deleteMany = async (where: Service_Request_Bool_Exp): Promise<number> => {
  const response = (await client.serviceRequestDeleteMany({where})).delete_service_request

  const affectedRows = response?.affected_rows

  if (typeof affectedRows !== 'number') {
    throw new ServerError('Service request deleteMany returned null', {where, response})
  }

  return affectedRows
}

const count = async (filter: Service_Request_Bool_Exp): Promise<number> => {
  const count = (await client.serviceRequestCount({filter})).service_request_aggregate.aggregate
    ?.count

  if (typeof count !== 'number') {
    throw new ServerError('Service request count returned invalid value', {filter, count})
  }

  return count
}

export const serviceRequestOrm = {
  getOneOrNull,
  getOne,
  getByIdOrNull,
  getById,
  getMany,
  updateOne,
  createOne,
  deleteMany,
  aggregate: {count},
  getManyWithTrademarks,
}
